/***
 *
 *   BRAND COLOUR CACHE
 *   Per-path session cache for the resolved onboarding frame colour.
 *
 *   The layout renders above the view that fetches the theme, so the frame's first
 *   paint necessarily happens before any colour is known. That would be a single
 *   harmless blink if the public Tranche 2 flow were an SPA — but it advances
 *   steps with window.location.replace (tranche2-page.js), a full document load
 *   per step, so an unseeded frame flashes the default purple on consent, on
 *   intake and again on biometric. Seeding React state synchronously from here
 *   removes the blink for every visit after the first.
 *
 *   Keyed on pathname alone: the step transitions differ only in the query string
 *   (`?step=intake&t=...`), so pathname is exactly the journey-stable part, and a
 *   cached colour stays scoped to the URL it was resolved for rather than becoming
 *   a global that could bleed one firm's brand onto another's page.
 *
 *   Every access is guarded: sessionStorage throws outright in some privacy modes,
 *   and a cache miss is always a correct answer here.
 *
 **********/

const PREFIX = "ic.brandColor:";

function currentKey() {
  return PREFIX + (typeof window === "undefined" ? "" : window.location.pathname);
}

// The exact shape formTheme.js `serialize()` emits. Validating against it rather
// than against `typeof === "string"` keeps that module's rule intact — colours are
// parsed and re-serialised before they reach the pixel path — across a round trip
// through storage, so a hand-edited entry cannot reach the layout's
// `linear-gradient(...)` template as raw text.
const SERIALIZED_RGB = /^rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)$/;

function isSerializedColor(value) {
  const match = typeof value === "string" && value.match(SERIALIZED_RGB);
  return !!match && match.slice(1).every((n) => Number(n) <= 255);
}

function isColorPair(value) {
  return !!(value && isSerializedColor(value.from) && isSerializedColor(value.to));
}

/* The seed. Returns null on a miss, on malformed storage, or when storage throws. */
export function readBrandColor() {
  try {
    const raw = window.sessionStorage.getItem(currentKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isColorPair(parsed) ? { from: parsed.from, to: parsed.to } : null;
  } catch (err) {
    return null;
  }
}

/* Called only once the payload has resolved, so an un-themed form clears the entry
   rather than leaving the previous colour to be re-seeded on the next load. */
export function writeBrandColor(value) {
  try {
    if (isColorPair(value)) {
      window.sessionStorage.setItem(
        currentKey(),
        JSON.stringify({ from: value.from, to: value.to })
      );
    } else {
      window.sessionStorage.removeItem(currentKey());
    }
  } catch (err) {
    // Caching is an optimisation; never let it break a render.
  }
}
